import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../../components/button";
import FitnessToolsResultCard from "../../../components/cards/FitnessToolsResultCard";
import Footer from "../../../components/footer";
import Header from "../../../components/header";
import Input from "../../../components/input";
import Loader from "../../../components/loader";
import OtherCalculatorSection from "../../../components/OtherCalculatorSection";
import useChartPeriod from "../../../hooks/useChartPeriod";
import {
  useCreateWHRMutation,
  useGetWHRQuery,
  useUpdateWHRMutation,
} from "../../../redux/Apis/Calculator/WHR";
import { useStaticData } from "../../../utils/data";
import { setMetaTags } from "../../../utils/helper";
import { useTranslate } from "../../../context/TranslationContext";

const WaistToHipRatio = () => {
  const { t } = useTranslate("whr");
  const navigate = useNavigate();
  const lang = useSelector((state) => state.language.lang);
  const { CALCULATORS, CALCULATOR_DATA } = useStaticData();
  const location = useLocation();
  const { user } = useSelector((state) => state.authSlice);
  const [gender, setGender] = useState("male");
  const [waist, setWaist] = useState("");
  const [hip, setHip] = useState("");
  const [result, setResult] = useState(null);
  const [classification, setClassification] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [createWHR] = useCreateWHRMutation();
  const [updateWHR] = useUpdateWHRMutation();
  const {
    data: whrHistoryData,
    refetch: refetchWHR,
    isLoading,
  } = useGetWHRQuery({ lang }, { skip: !user });

  const { period, dateRange, handlePeriodChange } = useChartPeriod();
  const { startDate, endDate } = dateRange;
  const {
    data: whrData,
    refetch: refetchWHRData,
    isLoading: loadingWhr,
  } = useGetWHRQuery(
    { startdate: startDate, enddate: endDate, lang },
    { skip: !startDate || !endDate || !user }
  );

  useEffect(() => {
    if (user) {
      refetchWHR();
    }
  }, [user, refetchWHR]);

  useEffect(() => {
    if (startDate && endDate && user) {
      refetchWHRData();
    }
  }, [startDate, endDate, user, refetchWHRData]);

  useEffect(() => {
    setMetaTags(
      "Traineego | Buy or Create Automated Courses & Tracks for Fitness & Wellness",
      "Create, manage, and sell your own courses and tracks, or buy high-quality content. Empower your career through automation and passive income today!"
    );
  }, []);

  useEffect(() => {
    if (whrHistoryData) {
      setHistoryData(whrHistoryData.data || []);
    }
  }, [whrHistoryData]);

  const calculateWHR = () => {
    if (!waist || !hip || parseFloat(waist) <= 0 || parseFloat(hip) <= 0) {
      toast.error(t("toasts.invalidInput"));
      return;
    }

    const whr = parseFloat(waist) / parseFloat(hip);
    const whrResult = whr.toFixed(2);
    setResult(whrResult);

    const riskClassification = getHealthRisk(whr, gender);
    setClassification(riskClassification);

    toast.success(t("toasts.calculateSuccess"));
    return whrResult;
  };

  const getHealthRisk = (whr, gender) => {
    if (gender === "male") {
      if (whr < 0.9) return t("riskLevels.low");
      else if (whr <= 0.99) return t("riskLevels.moderate");
      else return t("riskLevels.high");
    } else {
      if (whr < 0.8) return t("riskLevels.low");
      else if (whr <= 0.84) return t("riskLevels.moderate");
      else return t("riskLevels.high");
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t("history.notCalculated");
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryEntry = (entry) => {
    const createdAt = new Date(entry.createdAt).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return (
      <div className="d-flex justify-content-between align-items-center">
        <p className="mb-0">{createdAt}</p>
        <p className="mb-0 fw-bold">{entry.result}</p>
      </div>
    );
  };

  const handleSaveResult = async (type, result) => {
    if (!result.result) {
      toast.error(t("toasts.calculateFirst"));
      return;
    }

    if (!user) {
      toast.error(t("toasts.loginRequired"));
      // navigate("/auth/login");
      return;
    }

    try {
      const payload = {
        gender,
        waist,
        hip,
        result: result.result,
      };
      if (location?.state?.course) payload.course = location.state.course;
      const response = await createWHR({ payload, params: { lang } }).unwrap();
      toast.success(response.message || t("toasts.saveSuccess"));
      refetchWHR();
      refetchWHRData();
    } catch (error) {
      console.error("Error saving WHR:", error);
      toast.error(t("toasts.saveFail"));
    }
  };

  const handleDeleteLast = async () => {
    try {
      const latestEntry = [...historyData].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )[0];
      if (latestEntry) {
        const payload = {
          id: latestEntry._id,
          isactive: false,
          params: { lang },
        };
        const response = await updateWHR(payload).unwrap();
        toast.success(t("toasts.deleteSuccess"));
        refetchWHR();
        refetchWHRData();
      }
    } catch (error) {
      console.error("Error deleting last WHR:", error);
      toast.error(t("toasts.deleteFail"));
    }
  };

  const toggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  if (isLoading || loadingWhr) {
    <Loader loading={true} />;
  }

  return (
    <>
      <Header />
      <section className="banner" style={{ textAlign: "left" }}>
        <div className="container py-5">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="text-uppercase">{t("title")}</h1>
              <p className="mb-0 p-lg l-grey-text">{t("subtitle")}</p>
            </div>
          </div>

          <div className="row d-flex">
            <div
              className="col-md-12 mt-4 p-5"
              style={{ backgroundColor: "#161617", borderRadius: "2rem" }}
            >
              <div className="row">
                <div className="col-lg-8">
                  <h4 className="text-uppercase mb-4">
                    {t("calculateSectionTitle")}
                  </h4>

                  <div className="mb-3">
                    <Input
                      label={t("inputs.gender")}
                      type="select"
                      options={CALCULATOR_DATA.GENDER}
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      required
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.waist")}
                        placeholder={t("inputs.waistPlaceholder")}
                        required={true}
                        type="number"
                        value={waist}
                        onChange={(e) => setWaist(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        label={t("inputs.hip")}
                        placeholder={t("inputs.hipPlaceholder")}
                        required={true}
                        type="number"
                        value={hip}
                        onChange={(e) => setHip(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="row mt-5">
                    <div
                      className="col-12 bg-white py-2 px-4 d-flex justify-content-between align-items-center"
                      style={{ borderRadius: "0.5rem" }}
                    >
                      <p className="grey-text mb-0">
                        {t("calculateBox.label")}
                      </p>
                      <Button
                        text={t("calculateBox.button")}
                        onClick={calculateWHR}
                      />
                    </div>
                  </div>
                  {location.state?.returnTo && result && (
                    <div className="text-center mt-3">
                      <Button
                        text={t("continueLearning")}
                        className="site-btn border-btn"
                        style={{ width: "100%", maxWidth: "200px" }}
                        onClick={() =>
                          navigate(location.state.returnTo, {
                            state: {
                              ...location.state,
                              resultKey: `${location.state.sectionIndex}-${location.state.lectureIndex}-WHR`,
                              resultData: {
                                type: "WHR",
                                result: result,
                                value: `${result} (${classification})`,
                                timestamp: new Date().toISOString(),
                              },
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                <div className="col-12 col-lg-4 mt-4 mt-lg-0">
                  <div
                    className="h-100 p-3 p-md-4 d-flex flex-column justify-content-center"
                    style={{
                      border: "1px solid white",
                      borderRadius: "1rem",
                      background: "rgba(112,112,112,0.4)",
                    }}
                  >
                    <h6>
                      {result
                        ? `${t(
                            "resultBox.calculated"
                          )} ${result} (${classification})`
                        : t("resultBox.title")}
                    </h6>
                    <p className="p-sm">{t("resultBox.description")}</p>
                    <div
                      className="bg-white w-100 p-3 text-center mt-3"
                      style={{
                        border: "0.5px solid gray",
                        height: "auto",
                        borderRadius: "0.5rem",
                      }}
                    >
                      <p className="m-0 text-black">
                        {t("resultBox.noteMain")}
                      </p>
                      <p className="m-0 grey-text p-sm">
                        {t("resultBox.noteSub")}
                      </p>
                      <Button text={t("resultBox.cta")} to="/classes" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <FitnessToolsResultCard
              type="WHR"
              result={
                result
                  ? {
                      result,
                      classification,
                      timestamp: new Date().toISOString(),
                    }
                  : null
              }
              historyData={historyData}
              showHistory={showHistory}
              toggleHistory={toggleHistory}
              handleSaveResult={handleSaveResult}
              handleDeleteLast={handleDeleteLast}
              formatHistoryEntry={formatHistoryEntry}
              formatDateTime={formatDateTime}
              chartData={whrData?.data || []}
              period={period}
              handlePeriodChange={handlePeriodChange}
            />
          </div>
        </div>
      </section>
      <OtherCalculatorSection
        data={CALCULATORS.filter(
          (item) => item.route !== window.location.pathname
        )}
      />
      <Footer />
    </>
  );
};

export default WaistToHipRatio;
